import React, { useState, useEffect, useMemo } from "react";
import { xabRefApi } from "src/api/xabApi";
import { iconForCategory, isFaIconName } from "src/components/hazard/common";

// Emoji эсэхийг ялгах — ASCII үг биш бол emoji гэж үзнэ.
// (Material нэр «accessibility_new» нь ASCII үг тул emoji биш.)
const isAsciiWord = (s) => /^[a-z0-9_\-\s]+$/i.test(String(s || "").trim());

/**
 * CategoryIcon — аюулын хүчин зүйлийн icon-ыг ЛАВЛАХАД бүртгэснээр гаргана.
 *
 * Лавлах (xab.hazard_categories.icon) нь emoji, FontAwesome класс, эсвэл
 * Material нэр байж болно. Material нэрийг энэ төсөл ачаалдаггүй тул тэр
 * тохиолдолд нэрнээс нь FA5 icon-оор нөхнө (iconForCategory) — эс бөгөөс
 * "accessibility_new" гэсэн ТЕКСТ хэвээр харагдана.
 */
export function CategoryIcon({ icon, name, color, size = 12 }) {
  const c = color || "#8C8C8C";
  const raw = String(icon || "").trim();
  if (raw && isFaIconName(raw)) {
    return (
      <i
        className={raw.startsWith("fa-") ? "fas " + raw : raw}
        style={{ color: c, fontSize: size, flexShrink: 0 }}
      />
    );
  }
  if (raw && !isAsciiWord(raw)) {
    return (
      <span style={{ fontSize: size + 1, lineHeight: 1, flexShrink: 0 }}>
        {raw}
      </span>
    );
  }
  const f = iconForCategory(name);
  return (
    <i
      className={"fas " + f.icon}
      style={{ color: c, fontSize: size, flexShrink: 0 }}
    />
  );
}

/**
 * useWorkTypeColors — аюулын төрөл (ангилал)-ын нэр → лавлахад бүртгэсэн өнгө
 * (color_hex) map буцаана. Бүх эрсдэлийн хуудсанд төрлийг ижил өнгөөр ялгахад.
 */
export function useWorkTypeColors() {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    let alive = true;
    xabRefApi
      .getHazardCategories()
      .then((d) => alive && setCats(Array.isArray(d) ? d : []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return useMemo(() => {
    const m = {};
    cats.forEach((c) => {
      if (c && c.name) m[c.name] = c.color_hex || null;
    });
    return m;
  }, [cats]);
}

/**
 * useHazardCategoryColors — аюулын хүчин зүйл (ангилал)-ын лавлахын өнгийг
 * НЭР болон ID-гаар хайх map буцаана. (id-тэй бичлэгт ашиглана — hazard модуль.)
 *   { byName: {name→color}, byId: {id→{name,color}}, list: [...] }
 */
export function useHazardCategoryColors() {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    let alive = true;
    xabRefApi
      .getHazardCategories()
      .then((d) => alive && setCats(Array.isArray(d) ? d : []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return useMemo(() => {
    const byName = {};
    const byId = {};
    cats.forEach((c) => {
      if (!c) return;
      const color = c.color_hex || null;
      if (c.name) byName[c.name] = color;
      if (c.id != null)
        byId[c.id] = { name: c.name, color, icon: c.icon || null };
    });
    return { byName, byId, list: cats };
  }, [cats]);
}

/**
 * HazardCategoryTag — аюулын хүчин зүйлийг лавлахын өнгөт тэмдэглэгээ (icon)
 * + нэрээр харуулах. Бүх хуудсанд ижилхэн харагдана.
 */
export function HazardCategoryTag({ name, color, icon, style }) {
  if (!name) return null;
  const c = color || "#8C8C8C";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        padding: "2px 9px",
        borderRadius: 10,
        background: c + "18",
        color: c,
        border: `1px solid ${c}44`,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {/* Лавлах (hazard_categories)-д хадгалсан icon — emoji эсвэл FA класс */}
      <CategoryIcon icon={icon} name={name} color={c} size={12} />
      {name}
    </span>
  );
}

/**
 * WorkTypeBadge — аюулын төрлийг лавлахын өнгөөр харуулах badge.
 * color байхгүй бол саарал default.
 */
export function WorkTypeBadge({ name, color }) {
  if (!name) return "—";
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: "2px 10px",
        borderRadius: 10,
        display: "inline-block",
        whiteSpace: "nowrap",
        ...(color
          ? {
              background: color + "20",
              color,
              border: `1px solid ${color}55`,
            }
          : { background: "#F0F4F2", color: "#1B6B45" }),
      }}
    >
      {name}
    </span>
  );
}
